import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { dossiers, artAssets } from "../data/site";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import { SilentNightBanner } from "./SilentNightBanner";
import { SilentNightLore } from "./SilentNightLore";
import { WasteRunnerLore } from "./WasteRunnerLore";

const mapLayers = [
  {
    id: "routes",
    label: "RUNNER ROUTES",
    points: [
      {
        id: "route-a",
        x: "58%",
        y: "57%",
        title: "Safe Zone A Departure",
        text: "Primary ground departure point for runners leaving the central fortified sector.",
        threat: "1/5",
        travel: "START / 0 MIN",
      },
      {
        id: "route-b",
        x: "74%",
        y: "66%",
        title: "Safe Zone B Link",
        text: "Eastern handoff corridor connecting central supply traffic to Safe Zone B.",
        threat: "2/5",
        travel: "42 MIN",
      },
      {
        id: "route-c",
        x: "85%",
        y: "86%",
        title: "Safe Zone C Extension",
        text: "Southern extension route reaching Safe Zone C while skirting the lower quarantine field.",
        threat: "4/5",
        travel: "1H 35M",
      },
    ],
  },
  {
    id: "safehouses",
    label: "SAFEHOUSES",
    points: [
      {
        id: "safehouse-a",
        x: "58%",
        y: "57%",
        title: "Safehouse Alpha / Safe Zone A",
        text: "Central overnight shelter and supply cache positioned inside Safe Zone A.",
      },
      {
        id: "safehouse-b",
        x: "74%",
        y: "66%",
        title: "Safehouse Bravo / Safe Zone B",
        text: "Eastern cache and route-control point positioned inside Safe Zone B.",
      },
      {
        id: "safehouse-c",
        x: "85%",
        y: "86%",
        title: "Safehouse Charlie / Safe Zone C",
        text: "Southern recovery point for damaged runners and delayed cargo arriving through Sector 10.",
      },
    ],
  },
  {
    id: "hazards",
    label: "HAZARD ZONES",
    points: [
      {
        id: "sandworm-north",
        x: "24%",
        y: "36%",
        title: "Northern Sand Worm Area",
        text: "Sector 2 hazard field marked for active mutated sand worm movement.",
        threat: "5/5",
        travel: "+2H 10M DETOUR",
        dossierPath: "/character-book",
      },
      {
        id: "quarantine-upper",
        x: "71%",
        y: "36%",
        title: "Upper Quarantined Zone",
        text: "Hatched exclusion zone requiring full route avoidance or sealed-cabin transit.",
        threat: "5/5",
        travel: "+1H 40M DETOUR",
      },
      {
        id: "sandworm-west",
        x: "18%",
        y: "66%",
        title: "Western Sand Worm Area",
        text: "Sector 5 hazard field bordering abandoned cities and broken western roads.",
        threat: "4/5",
        travel: "+1H 15M DETOUR",
        dossierPath: "/character-book",
      },
      {
        id: "sandworm-central",
        x: "67%",
        y: "77%",
        title: "Central Sand Worm Area",
        text: "Sector 7 threat pocket between Safe Zone B and the southern route extension.",
        threat: "4/5",
        travel: "+55M DETOUR",
        dossierPath: "/character-book",
      },
      {
        id: "quarantine-lower",
        x: "88%",
        y: "77%",
        title: "Lower Quarantined Zone",
        text: "Lower hatched exclusion zone that forces Safe Zone C traffic onto the mapped runner corridor.",
        threat: "5/5",
        travel: "+2H 00M DETOUR",
      },
    ],
  },
  {
    id: "threats",
    label: "WASTELAND THREATS",
    points: [
      {
        id: "mutated-deer-bears",
        x: "12%",
        y: "45%",
        title: "Mutated Deer Bear Territory",
        text: "Northwestern timber ruins where deer bear packs den. They charge engine noise on sight and do not stop.",
        threat: "4/5",
        travel: "+1H 05M DETOUR",
      },
      {
        id: "border-patrol",
        x: "48%",
        y: "27%",
        title: "Border Patrol Checkpoint Line",
        text: "Government checkpoint chain guarding the northern pass. Papers scanned twice, cargo opened once — bribes logged.",
        threat: "3/5",
        travel: "+40M CHECKPOINT",
      },
      {
        id: "cyborg-bounty-hunters",
        x: "80%",
        y: "15%",
        title: "Cyborg Bounty Hunter Circuit",
        text: "Northeastern hunting circuit. Contract cyborgs track runner transponders for credits — jam your signal or run dark.",
        threat: "5/5",
        travel: "+1H 50M EVASION",
      },
      {
        id: "mutated-coyotes",
        x: "33%",
        y: "55%",
        title: "Mutated Coyote Pack Range",
        text: "West-central scrubland ruled by coyote packs. They shadow lone runners after dusk and test every fence line.",
        threat: "3/5",
        travel: "+35M DETOUR",
      },
      {
        id: "mutated-vultures",
        x: "90%",
        y: "47%",
        title: "Mutated Vulture Nesting Cliffs",
        text: "Eastern cliff colony. Vultures dive at anything that stops moving — keep the convoy rolling through Sector 9.",
        threat: "2/5",
        travel: "+25M DETOUR",
      },
      {
        id: "sand-pirates",
        x: "40%",
        y: "80%",
        title: "Sand Pirate Ambush Corridor",
        text: "Southern dune crossing stripped by pirate skiffs. They take cargo, batteries, and boots — in that order.",
        threat: "4/5",
        travel: "+1H 20M DETOUR",
      },
    ],
  },
  {
    id: "landmarks",
    label: "HIDDEN LANDMARKS",
    points: [
      {
        id: "kaelen-workshop",
        x: "55%",
        y: "40%",
        title: "Kaelen's Workshop — Old Atlanta Underground",
        text: "Subterranean salvage bay in the ruins of the Old Atlanta Underground where Kaelen pieced Dolo back together in 2355. Unlisted on every League grid; runners find it by whisper, not by map.",
        threat: "1/5",
        travel: "ACCESS: RUNNER-CLEARED ONLY",
        dossierPath: "/character-book",
      },
      {
        id: "shattered-concrete-memorial",
        x: "62%",
        y: "22%",
        title: "Night of the Shattered Concrete — Memorial Ground",
        text: "The Grand Capitol lawn where five protesters fell on July 1st, 2035. The red-stained ball that started AL Riotball rests here under poured concrete and permanent League surveillance. Mourners leave small red marks; the League paints over them by morning.",
        threat: "2/5",
        travel: "CURFEW: 22:00-06:00",
        dossierPath: "/riotball#origins",
        dossierLabel: "READ THE ORIGINS ARCHIVE",
      },
    ],
  },
];

export function DossierSection() {
  const [activeLayerId, setActiveLayerId] = useState("routes");
  const [activePointId, setActivePointId] = useState("route-a");
  const activeLayer = mapLayers.find((item) => item.id === activeLayerId) || mapLayers[0];
  const activePoint = activeLayer.points.find((item) => item.id === activePointId) || activeLayer.points[0];

  const selectMapLayer = (layerId) => {
    const layer = mapLayers.find((item) => item.id === layerId) || mapLayers[0];
    setActiveLayerId(layer.id);
    setActivePointId(layer.points[0].id);
  };

  return (
    <section id="lore" className="dossier-section" data-testid="dossier-section">
      <SectionHeading
        eyebrow="04 / LORE & ARCHIVES"
        title="The archive now has coordinates."
        text="The official Atlanta sector map establishes the world. Dolo, General Deacon Ramirez, the Safe Zone Army, and New Atlanta defense systems now hold live dossiers."
      />
      <Reveal className="world-map-feature" delay={0.08}>
        <figure className="world-map-frame" data-testid="atlanta-sector-map">
          <div className="map-image-wrap">
            <img loading="lazy" src={artAssets.sectorMap} alt="Atlanta Sectors 1-10 post-second apocalypse new world map" />
            <svg
              className={`route-layer-overlay ${activeLayer.id === "routes" ? "is-active" : ""}`}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M58 57 C62 60 68 63 74 66 C79 72 82 79 85 86" />
              <path d="M58 57 C53 51 50 43 48 27" />
              <path d="M74 66 C68 70 67 74 67 77" />
            </svg>
            {activeLayer.points.map((point) => (
              <button
                type="button"
                key={point.id}
                className={`map-layer-hotspot ${activePoint.id === point.id ? "is-active" : ""}`}
                style={{ left: point.x, top: point.y }}
                onClick={() => setActivePointId(point.id)}
                data-testid={`map-point-${point.id}`}
                aria-label={`Show ${point.title}`}
                aria-pressed={activePoint.id === point.id}
              >
                <span />
              </button>
            ))}
          </div>
        </figure>
        <div className="world-map-copy">
          <span>NEW WORLD MAP / 2355</span>
          <h3>ATLANTA SECTORS 1–10</h3>
          <p>
            Tap the tactical markers to reveal runner routes, safehouse clusters, quarantine zones, and the wasteland's
            worst company — sand pirates, border patrols, cyborg bounty hunters, and mutated wildlife.
          </p>
          <div className="map-layer-detail" data-testid="active-map-layer-detail">
            <span>{activeLayer.label}</span>
            <strong>{activePoint.title}</strong>
            <p>{activePoint.text}</p>
            {activePoint.threat ? (
              <div className="map-point-metrics">
                <div data-testid="map-point-threat">
                  <small>THREAT RATING</small>
                  <strong>{activePoint.threat}</strong>
                </div>
                <div data-testid="map-point-travel">
                  <small>TRAVEL ESTIMATE</small>
                  <strong>{activePoint.travel}</strong>
                </div>
              </div>
            ) : null}
            {activePoint.dossierPath ? (
              <Link to={activePoint.dossierPath} className="map-dossier-link" data-testid="map-dossier-link">
                {activePoint.dossierLabel || "OPEN FIELD DOSSIER"} <ArrowUpRight size={15} />
              </Link>
            ) : null}
          </div>
          <div className="map-tags" aria-label="Map layer controls">
            {mapLayers.map((layer) => (
              <button
                type="button"
                key={layer.id}
                className={activeLayer.id === layer.id ? "is-active" : ""}
                onClick={() => selectMapLayer(layer.id)}
                data-testid={`map-layer-tab-${layer.id}`}
                aria-pressed={activeLayer.id === layer.id}
              >
                {layer.label}
              </button>
            ))}
          </div>
        </div>
      </Reveal>
      <SilentNightBanner />
      <SilentNightLore />
      <WasteRunnerLore />
      <div className="dossier-grid">
        {dossiers.map((item, index) => (
          <Reveal key={item.id} delay={index * 0.06}>
            <Link to="/character-book" className="dossier-card" data-testid={`dossier-card-${item.id}`}>
              <span>{item.code}</span>
              {item.image ? (
                <div className="portrait-thumb">
                  <img loading="lazy" src={item.image} alt={`${item.name} official character artwork`} />
                </div>
              ) : (
                <div className="portrait-lock" />
              )}
              <h3>{item.name}</h3>
              <small>{item.role}</small>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
